import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Elements, injectStripe } from 'react-stripe-elements';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { formatNumber } from 'utils/formating';
import { PaymentTypes, SubscriptionTypes } from 'appConstants';
import { useFetchUpgradeSubscriptionsList } from 'hooks/useFetchUpgradeSubscriptionsList';
import { SubscriptionsParser, UpgradeSubscriptionFormValues } from './models';
import { UpgradeSubscriptionsForm } from './UpgradeSubscriptionsForm';
import { PopoutMenu } from '../../components/popupsAndPanels/PopoutMenu';
import { PopoutMenuFooter } from '../../components/popupsAndPanels/PopoutMenuFooter';
import { PopoutMenuHeader } from '../../components/popupsAndPanels/PopoutMenuHeader';
import FormTextField from 'components/formComponents/FormTextField';
import { useToast } from 'hooks/useToast';
import FormGeneralError from 'components/formComponents/FormGeneralError';

const FORM_FIELD_PLACEHOLDER = 'Enter PO number (optional)';

enum UpgradeStep {
  SELECT_TYPE,
  PAY,
}

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  menuTitle: string;
  id: number;
  stripe?: any;
  setUpdateList: React.Dispatch<React.SetStateAction<boolean>>;
};

export const InnerUpgradeSubscriptions: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  menuTitle,
  id,
  stripe,
  setUpdateList,
}) => {
  const [upgradeStep, setUpgradeStep] = useState(UpgradeStep.SELECT_TYPE);
  const [error, setError] = useState<string>('');
  const [financeEmail, setFinanceEmail] = useState<any>('');

  const handlePayByCardToast = useToast('Your payment was successful');
  const handlePayByInvoiceToast = useToast(
    'You will shortly be contacted by email'
  );

  const { subscriptionsListInfo, errors } =
    useFetchUpgradeSubscriptionsList(id);

  useEffect(() => {
    if (!subscriptionsListInfo) return;
    setFinanceEmail(subscriptionsListInfo.financeEmail);
  }, [subscriptionsListInfo]);  

  const handleMenuClose = () => {
    togglePanelOpen(false);
  };

  const validationSchema = Yup.object({
    FinanceEmail: Yup.string().email('Please enter a valid email').required('A finance email is required').nullable(),
  });

  const upgradeByInvoice = async (
    values: UpgradeSubscriptionFormValues,
    setSubmitting: (status: boolean) => void
  ) => {
    const result = await SubscriptionsApi.upgradeByInvoice(values);
    if (result.data) {
      setSubmitting(false);
      handlePayByInvoiceToast();
      setUpdateList(true);
      handleMenuClose();
    }

    if (result.error) {
      setSubmitting(false);
      setError(result.error.message);
    }
  };

  const createStripeToken = (
    values: UpgradeSubscriptionFormValues,
    setSubmitting: (status: boolean) => void
  ) =>
    stripe
      .createToken({ name: values.cardholderName })
      .then(({ token, error }: any) => {
        if (token) {
          upgradeByCard(token, values, setSubmitting);
        }
        if (error) {
          setSubmitting(false);
          setError('Sorry, something went wrong. Please try again.');
        }
      });

  const upgradeByCard = async (
    token: any,
    values: UpgradeSubscriptionFormValues,
    setSubmitting: (status: boolean) => void
  ) => {
    const result = await SubscriptionsApi.upgradeByCard(values, token);
    if (result.data) {
      const status = result.data.status;
      if (status === 'succeeded') {
        handlePayByCardToast();
        setUpdateList(true);
        handleMenuClose();
      }

      if (status === 'requires_action') {
        const clientSecret = result.data.clientSecret;
        const tid = result.data.tid;
        handleCardAction(clientSecret, setSubmitting, tid);
      }
    }
    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
    }
  };

  const handleCardAction = (
    clientSecret: string,
    setSubmitting: (status: boolean) => void,
    tid: string
  ) => {
    stripe.handleCardAction(clientSecret).then((res: any) => {
      if (res.error) {
        setSubmitting(false);
      }
      updateAfter3DSecure(res, tid, setSubmitting);
    });
  };

  const updateAfter3DSecure = async (
    values: any,
    tid: string,
    setSubmitting: (status: boolean) => void
  ) => {
    const result = await SubscriptionsApi.updateAfter3DSecureUpgrade({
      ...values,
      tid,
    });

    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
    } else {
      handlePayByCardToast();
      setUpdateList(true);
      handleMenuClose();
    }
  };

  const handleFormSubmission = (
    values: UpgradeSubscriptionFormValues,
    {
      setSubmitting,
      setFieldError,
    }: FormikHelpers<UpgradeSubscriptionFormValues>
  ) => {
    if (upgradeStep === UpgradeStep.SELECT_TYPE) {
      if (!values.package?.packageId) {
        setSubmitting(false);

        setError('Please select a package');
        return;
      }
      setUpgradeStep(UpgradeStep.PAY);
      setSubmitting(false);
    }
    if (upgradeStep === UpgradeStep.PAY) {
      if (values.paymentType === PaymentTypes.PAY_BY_CARD) {
        if (!values.cardholderName) {
          setFieldError('cardholderName', 'Please provide the cardholder name');
          setSubmitting(false);
          return;
        }
        if (!values.paymentType) {
          setFieldError('paymentType', 'Please provide a type of payment');
          setSubmitting(false);
          return;
        }
        createStripeToken(values, setSubmitting);
      }
      if (values.paymentType === PaymentTypes.PAY_BY_INVOICE) {
        upgradeByInvoice(values, setSubmitting);
      }
    }
  };

  return (
    <Formik
      initialValues={SubscriptionsParser.createEmptyUpgradePackages(financeEmail)}
      onSubmit={(values, formikHelpers) =>
        handleFormSubmission(values, formikHelpers)
      }
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {({ values, submitForm, isSubmitting, setFieldValue }) => (
        <Form>
          <PopoutMenu
            isPopup={false}
            isOpen={panelOpen}
            handleClose={handleMenuClose}
            className="SubscriptionsPopoutMenu"
            position="right"
            loading={!subscriptionsListInfo && !errors}
            error={!!errors}
          >
            <PopoutMenuHeader title={menuTitle} handleClose={handleMenuClose} />
            <div className="PopoutMenuContent">
              <FormGeneralError message={error} />
              <FormTextField
                field={'poNumber'}
                placeholder={FORM_FIELD_PLACEHOLDER}
                required={false}
              />
              <div className="LineSpacer3"></div>
              <p>Enter email address to send card receipt / invoice to:</p>
              <FormTextField
                field={'FinanceEmail'}
                placeholder={'Email address'}
                required={true}
                value={values.FinanceEmail}
              />              
              {upgradeStep === UpgradeStep.SELECT_TYPE ? (
                <>
                  {subscriptionsListInfo &&
                    subscriptionsListInfo.upgradeList &&
                    subscriptionsListInfo.upgradeList.map(
                      (subscription, index) => (
                        <label
                          className={`SubscriptionOption ${
                            values.package?.packageId === subscription.packageId
                              ? 'Selected'
                              : ''
                          }`}
                          key={index}
                        >
                          <div className="Left">
                            <input
                              type="radio"
                              name="subscription-type"
                              className="HiddenInput"
                              checked={
                                values.package?.packageId ===
                                subscription.packageId
                              }
                              onChange={(e) => {
                                if (
                                  values.package?.packageId ===
                                  subscription.packageId
                                ) {
                                  setFieldValue('package', null);
                                } else setFieldValue('package', subscription);
                              }}
                            />
                            <label className="CustomInput" />
                            <div className="Text">
                              <h2>{subscription.subscription}</h2>
                              <p>
                                Accessible for
                                {SubscriptionTypes.STANDARD ===
                                subscription.type
                                  ? ' 1 year'
                                  : SubscriptionTypes.INVESTEMENT ===
                                    subscription.type
                                  ? ' 3 years'
                                  : null}
                              </p>
                            </div>
                          </div>
                          <h2>£{formatNumber(subscription.price)}</h2>
                        </label>
                      )
                    )}
                </>
              ) : upgradeStep === UpgradeStep.PAY && values.package ? (
                <UpgradeSubscriptionsForm
                  selectedPackage={values.package}
                  vatRate={
                    subscriptionsListInfo?.vatRate
                      ? parseInt(subscriptionsListInfo.vatRate)
                      : null
                  }
                />
              ) : null}
            </div>
            <PopoutMenuFooter
              primaryButtonText="Upgrade"
              secondaryButtonText="Cancel"
              handlePrimaryButtonClick={submitForm}
              handleSecondaryButtonClick={handleMenuClose}
              isSubmitting={isSubmitting}
            />
          </PopoutMenu>
        </Form>
      )}
    </Formik>
  );
};

const UpgradeSubscriptionsWithStripe = injectStripe(InnerUpgradeSubscriptions);

export const UpgradeSubscriptions: React.FC<Props> = (props) => {
  return (
    <Elements>
      <UpgradeSubscriptionsWithStripe {...props} />
    </Elements>
  );
};
